import type { LucideIcon } from 'lucide-react';
import {
  Boxes,
  Split,
  Filter,
  Hourglass,
  Megaphone,
  Repeat,
  Shuffle,
} from 'lucide-react';

export type WorkflowNodePaletteBuiltinKind =
  | 'for-each'
  | 'filter'
  | 'debounce'
  | 'mapper'
  | 'emit-event'
  | 'bucket'
  | 'group-by';

export type WorkflowNodePaletteBuiltinItem = {
  readonly id: string;
  readonly label: string;
  readonly description?: string;
  readonly keywords?: readonly string[];
  readonly icon: LucideIcon;
  readonly kind: WorkflowNodePaletteBuiltinKind;
};

export type WorkflowNodePaletteSection =
  | { readonly id: 'agents'; readonly label: 'Agents' }
  | { readonly id: 'tools'; readonly label: 'Tools' }
  | { readonly id: 'workflows'; readonly label: 'Workflows' }
  | {
      readonly id: 'flow-control';
      readonly label: 'Flow control';
      readonly items: readonly WorkflowNodePaletteBuiltinItem[];
    };

/**
 * Registry of node palette sections. Add a section or builtin item here to
 * expose a new workflow node type in the add-node modal.
 */
export const WORKFLOW_NODE_PALETTE_SECTIONS: readonly WorkflowNodePaletteSection[] =
  [
    { id: 'agents', label: 'Agents' },
    { id: 'tools', label: 'Tools' },
    { id: 'workflows', label: 'Workflows' },
    {
      id: 'flow-control',
      label: 'Flow control',
      items: [
        {
          id: 'for-each',
          label: 'For each',
          description: 'Iterate over a collection',
          keywords: ['loop', 'iterate', 'foreach'],
          icon: Repeat,
          kind: 'for-each',
        },
        {
          id: 'filter',
          label: 'Filter',
          description: 'Continue only if a condition is met',
          keywords: ['condition', 'filter', 'where', 'if', 'gate'],
          icon: Filter,
          kind: 'filter',
        },
        {
          id: 'debounce',
          label: 'Debounce',
          description: 'Continue once per time window for each key',
          keywords: [
            'debounce',
            'once',
            'throttle',
            'rate limit',
            'suppress',
            'dedupe',
            'weekly',
            'daily',
            'repeat',
          ],
          icon: Hourglass,
          kind: 'debounce',
        },
        {
          id: 'mapper',
          label: 'Mapper',
          description: 'Collect fields from any earlier step into one input',
          keywords: [
            'map',
            'mapper',
            'context',
            'fields',
            'input',
            'reshape',
            'trigger',
          ],
          icon: Shuffle,
          kind: 'mapper',
        },
        {
          id: 'emit-event',
          label: 'Emit event',
          description: 'Publish a named event for other workflows',
          keywords: ['event', 'emit', 'publish', 'signal', 'notify'],
          icon: Megaphone,
          kind: 'emit-event',
        },
        {
          id: 'bucket',
          label: 'Bucket',
          description:
            'Accumulate results across runs, then flush downstream once',
          keywords: [
            'bucket',
            'accumulate',
            'batch',
            'collect',
            'digest',
            'aggregate',
            'window',
          ],
          icon: Boxes,
          kind: 'bucket',
        },
        {
          id: 'group-by',
          label: 'Group by',
          description:
            'Split a list into groups by a field; add a For each to run per group',
          keywords: [
            'group',
            'group by',
            'split',
            'partition',
            'per user',
            'per customer',
            'slice',
            'fan out',
          ],
          icon: Split,
          kind: 'group-by',
        },
      ],
    },
  ];
